// Layer 3 seed (static): Bunker Hill readings — numeric source of truth for
// constraints, stream specs, unit key params, KPI triads, KPV and reagent rows.
// Hand-transcribed once from bunker-hill-seed string rows (Module 1 §8.2 / §8.4,
// deck slide 8, role tables). Provenance text preserved; [syn] where noted.

import type { Reading } from '@/versions/v1/schema/reading';
import { smelterPenaltyLimit } from './bunker-hill-smelter-terms';

// D14: BM01's fresh-feed displays are hourly projections of this measured
// daily mass, not an independently seeded circulating-load stream.
const PLANT_ORE_READING: Reading = {
  id: 'rd-kpi-ore-td', tag: 'kpi-ore-td', label: 'Ore processed',
  kind: 'rate', scope: { kind: 'plant' }, unit: 'st/d',
  value: 1720, design: 1800, target: 1440, targetBasis: 'budget', latency: 'shift',
  provenance: 'FD Month-4 ramp: 60 stph → 1,440 stpd, 80% of 1,800 design (deck design says 1,800) [Budget track: Month-4 ramp target adopted under pl.D1 #142, 2026-09-09]',
};
const MILL_FEED_BAND = { lo: 55, hi: 90 };
/** Read-only aliases used by cursor projections; no second historian series. */
export const HOURLY_FEED_READING_IDS: readonly string[] = ['rd-kpv-bm-feed', 'rd-unit-bm01-feed'];

/** The divisor that turns the measured daily mass into the hourly feed basis:
 *  `rd-kpv-bm-feed` is `rd-kpi-ore-td ÷ 24` (D14). One definition, read by
 *  `series.derive.ts` (`valueAt`) and by the Process route's chart projection
 *  (#735), so a figure and the series behind it can never drift apart. */
export const HOURLY_FEED_DIVISOR = 24;

export type UnitKeyParamSegment =
  | { id: string; prefix?: string; withUnit?: boolean }
  | { verbatim: string };

export type UnitKeyParamCopy = {
  verbatim?: string;
  segments?: readonly UnitKeyParamSegment[];
};

/** Verbatim constraint projection strings the numbers cannot reproduce (reading.md § Prose). */
export const CONSTRAINT_COPY: Record<
  string,
  { dcsRange?: string; normalRange?: string; alarms?: string }
> = {
  'rd-c-brg': {
    normalRange: 'High alarm 650 kPa',
    alarms: 'H 650 kPa',
  },
  'rd-c-cyc-p': { normalRange: 'Design 80 – 140 kPa' },
  'rd-c-pb-ph': {
    normalRange: 'Target 7.5 – 8.5',
    alarms: 'LL/L/H/HH 6.5/7.0/10.0/11.0',
  },
  'rd-c-zn-ph': {
    normalRange: 'Target 11.0 – 12.0',
    alarms: 'LL/L/H/HH 10.0/10.5/12.5/13.0',
  },
  'rd-c-rake': {
    normalRange: 'High alarm 70%',
    alarms: 'H 70%',
  },
};

/** Per-row display overrides for constraint rows whose source text is not a number (O6). */
export const CONSTRAINT_DISPLAY_OVERRIDE: Record<
  string,
  { dcsRange?: string; normalRange?: string }
> = {
  'rd-c-cyc-n': {
    dcsRange: '5 installed',
    normalRange: '2 (<50 tph) → 3 (50–70) → 4 (>70, design) — 1 spare',
  },
  'rd-c-water': {
    normalRange: 'In 451 gpm / Out 456 gpm (~5 gpm makeup)',
  },
};

export const UNIT_KEY_PARAM_COPY: Record<string, UnitKeyParamCopy> = {
  CR01: {
    segments: [
      { id: 'rd-unit-cr01-rate', withUnit: true },
      { id: 'rd-unit-cr01-avail', withUnit: true },
    ],
  },
  BM01: {
    segments: [
      { id: 'rd-unit-bm01-feed', withUnit: true },
      { id: 'rd-spec-p80', prefix: 'P80 ', withUnit: true },
    ],
  },
  CY01: {
    segments: [
      { id: 'rd-unit-cy01-sol', withUnit: true },
      { id: 'rd-unit-cy01-p', withUnit: true },
    ],
  },
  RG01: {
    segments: [
      { id: 'rd-unit-rg01-rate', withUnit: true },
      { id: 'rd-unit-rg01-p80', withUnit: true },
    ],
  },
  PB01: {
    segments: [
      { id: 'rd-unit-pb01-ph', prefix: 'pH ', withUnit: false },
      { id: 'rd-unit-pb01-rec', prefix: 'rec ', withUnit: true },
    ],
  },
  ZN01: {
    segments: [
      { id: 'rd-unit-zn01-ph', prefix: 'pH ', withUnit: false },
      { id: 'rd-unit-zn01-rec', prefix: 'rec ', withUnit: true },
    ],
  },
  'TK-ZN': {
    segments: [
      { id: 'rd-unit-tkzn-uf', withUnit: true },
      { verbatim: 'OF clear' },
    ],
  },
  'FL-ZN': {
    segments: [{ id: 'rd-unit-flzn-moist', withUnit: true }],
  },
  PS01: {
    segments: [{ id: 'rd-unit-ps01-paste', withUnit: true }],
  },
};

export const STREAM_SPEC_COPY: Record<
  string,
  { consequence: string; provenance?: string; target?: string }
> = {
  'rd-spec-p80': {
    consequence:
      'Coarser grind starves liberation — flotation kinetics collapse downstream.',
    provenance: 'Module doc §8.4 — resolved to PFS design 74 µm: deck design says 75 µm',
  },
  'rd-spec-pb-grade': {
    consequence: 'Below spec reduces smelter payability.',
    provenance: 'Module doc §8.4 — resolved to PFS: deck design says 65.0%',
  },
  'rd-spec-ag-grade': {
    consequence: 'Silver is the payability kicker on the Pb concentrate.',
    provenance: 'Module doc §8.4 — resolved to PFS: deck design says 32.0 oz/t',
  },
  'rd-spec-pb-as': {
    consequence: 'Above threshold the smelter applies penalties per contract.',
    provenance: 'Deck slide 8 smelter contract limits',
  },
  'rd-spec-zn-grade': {
    consequence: 'Below spec reduces smelter payability.',
    provenance: 'Module doc §8.4 — resolved to PFS: deck design says 52.0%',
  },
  'rd-spec-zn-pb': {
    consequence: 'Pb in Zn concentrate is a smelter penalty and a Pb recovery loss.',
    provenance: 'Module doc §8.4',
  },
  'rd-spec-zn-as': {
    consequence: 'Above threshold the smelter applies penalties per contract.',
    provenance: 'Deck slide 8 smelter contract limits',
  },
  'rd-spec-tails-pb': {
    consequence: 'Recoverable Pb leaving the plant forever — recovery loss.',
    provenance: 'Module doc §8.4',
  },
  'rd-spec-tails-zn': {
    consequence: 'Recoverable Zn leaving the plant forever — recovery loss.',
    provenance: 'Derived from Zn recovery target at PFS head grade — V8 sheet says 0.5%',
  },
};

export const READINGS: readonly Reading[] = [
  {
    id: 'rd-c-crush',
    tag: 'WIC1101',
    label: 'Secondary crushing throughput',
    kind: 'rate',
    scope: { kind: 'subprocess', id: 'sp-crush' },
    unit: 'tph',
    value: 75,
    design: 75,
    higherIsBetter: true,
    dcsRange: { lo: 0, hi: 180 },
    band: { lo: 60, hi: 150 },
    latency: 'online',
    provenance: 'Module 1 §8.2 Appendix B',
  },
  {
    id: 'rd-c-feed',
    tag: 'WIC1201A',
    label: 'Ball mill feed rate (WIC1201A)',
    kind: 'rate',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: 'stph',
    value: 75,
    design: 75,
    higherIsBetter: true,
    dcsRange: { lo: 0, hi: 120 },
    band: MILL_FEED_BAND,
    latency: 'online',
    provenance: 'Module 1 §8.2 Appendix B',
  },
  {
    id: 'rd-c-kw',
    tag: 'JIC1210',
    label: 'Ball mill motor power (JIC1210)',
    kind: 'power',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: 'kW',
    dcsRange: { lo: 0, hi: 1500 },
    band: { lo: 700, hi: 1050 },
    latency: 'online',
    provenance: 'Module 1 §8.2 Appendix B',
  },
  {
    id: 'rd-c-brg',
    tag: 'PI1205',
    label: 'Ball mill bearing pressure',
    kind: 'pressure',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: 'kPa',
    dcsRange: { lo: 0, hi: 800 },
    alarms: { H: 650 },
    latency: 'online',
    provenance: 'Module 1 §8.2 Appendix B',
  },
  {
    id: 'rd-c-cyc-p',
    tag: 'PI1310',
    label: 'Cyclone feed header pressure (PI1310)',
    kind: 'pressure',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: 'kPa',
    dcsRange: { lo: 0, hi: 200 },
    band: { lo: 80, hi: 140 },
    latency: 'online',
    provenance: 'Module 1 §8.2 Appendix B',
  },
  {
    id: 'rd-c-cyc-n',
    tag: 'CY-CLSTR',
    label: 'Cyclones in service',
    kind: 'count',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: 'installed',
    value: 4,
    dcsRange: { hi: 5 },
    latency: 'online',
    provenance: 'Module 1 §8.2 Appendix B',
  },
  {
    id: 'rd-c-pb-ph',
    tag: 'AIC4001',
    label: 'Pb rougher feed pH (AIC4001)',
    kind: 'ph',
    scope: { kind: 'subprocess', id: 'sp-pb' },
    unit: 'pH',
    dcsRange: { lo: 4.0, hi: 12.0 },
    band: { lo: 7.5, hi: 8.5 },
    alarms: { LL: 6.5, L: 7.0, H: 10.0, HH: 11.0 },
    decimals: 1,
    latency: 'online',
    provenance: 'Module 1 §8.2 Appendix B',
  },
  {
    id: 'rd-c-zn-ph',
    tag: 'AIC5002',
    label: 'Zn conditioning tank pH (AIC5002)',
    kind: 'ph',
    scope: { kind: 'subprocess', id: 'sp-zn' },
    unit: 'pH',
    dcsRange: { lo: 4.0, hi: 14.0 },
    band: { lo: 11.0, hi: 12.0 },
    alarms: { LL: 10.0, L: 10.5, H: 12.5, HH: 13.0 },
    decimals: 1,
    latency: 'online',
    provenance: 'Module 1 §8.2 Appendix B',
  },
  {
    id: 'rd-c-rake',
    tag: 'OT-200',
    label: 'Thickener rake torque',
    kind: 'ratio',
    scope: { kind: 'subprocess', id: 'sp-filter' },
    unit: '%',
    dcsRange: { lo: 0, hi: 100 },
    alarms: { H: 70 },
    latency: 'online',
    provenance: 'Module 1 §8.2 Appendix B',
  },
  {
    id: 'rd-c-water',
    tag: 'FIT-WATER',
    label: 'Process water tank balance',
    kind: 'rate',
    scope: { kind: 'subprocess', id: 'sp-water' },
    unit: 'gpm',
    latency: 'shift',
    provenance: 'Module 1 §8.2 Appendix B',
  },
  {
    id: 'rd-spec-p80',
    tag: 'SA01',
    label: 'Grind size P80',
    kind: 'size',
    scope: { kind: 'stream', id: 'st-cyc-of' },
    unit: 'µm',
    value: 82,
    design: 74,
    higherIsBetter: false,
    latency: 'shift',
    provenance: 'Module doc §8.4',
  },
  {
    id: 'rd-spec-pb-grade',
    tag: 'SA03',
    label: 'Pb grade',
    kind: 'quality',
    scope: { kind: 'stream', id: 'st-pbcon' },
    unit: '% Pb',
    value: 67.1,
    design: 67.0,
    higherIsBetter: true,
    decimals: 1,
    latency: 'rush',
    provenance: 'Module doc §8.4',
    provenanceByField: { spec: 'Module doc §8.4 — resolved to PFS: deck design says 65.0%' },
  },
  {
    id: 'rd-spec-ag-grade',
    tag: 'SA03',
    label: 'Ag content',
    kind: 'quality',
    scope: { kind: 'stream', id: 'st-pbcon' },
    unit: 'oz/ton',
    value: 12.14,
    design: 12.13,
    higherIsBetter: true,
    decimals: 2,
    latency: 'rush',
    provenance: 'Module doc §8.4',
    provenanceByField: { spec: 'Module doc §8.4 — resolved to PFS: deck design says 32.0 oz/t' },
  },
  {
    id: 'rd-spec-pb-as',
    tag: 'SA03',
    label: 'As (penalty element)',
    kind: 'quality',
    scope: { kind: 'stream', id: 'st-pbcon' },
    unit: '%',
    value: 0.32,
    spec: { hi: 0.5 },
    higherIsBetter: false,
    decimals: 2,
    latency: 'rush',
    provenance: 'Deck slide 8 smelter contract limits',
  },
  {
    id: 'rd-spec-zn-grade',
    tag: 'SA04',
    label: 'Zn grade',
    kind: 'quality',
    scope: { kind: 'stream', id: 'st-zncon' },
    unit: '% Zn',
    value: 58.4,
    design: 58.0,
    higherIsBetter: true,
    decimals: 1,
    latency: 'rush',
    provenance: 'Module doc §8.4',
    provenanceByField: { spec: 'Module doc §8.4 — resolved to PFS: deck design says 52.0%' },
  },
  {
    id: 'rd-spec-zn-pb',
    tag: 'SA04',
    label: 'Pb contamination',
    shortLabel: 'Pb in Zn con',
    kind: 'quality',
    scope: { kind: 'stream', id: 'st-zncon' },
    unit: '%',
    value: 2.6,
    spec: { hi: smelterPenaltyLimit('Zn', 'Pb') },
    higherIsBetter: false,
    latency: 'rush',
    provenance: 'Module doc §8.4',
  },
  {
    id: 'rd-zn-cond-residence',
    tag: 'ZN-RT',
    label: 'Zn conditioner residence',
    kind: 'rate',
    scope: { kind: 'subprocess', id: 'sp-zn' },
    unit: 'min',
    value: 42,
    design: 40,
    higherIsBetter: false,
    latency: 'online',
    provenance: '[syn] 40 min holdup × design/current flow — advisory-depth #327',
  },
  {
    id: 'rd-spec-zn-as',
    tag: 'SA04',
    label: 'As (penalty element)',
    kind: 'quality',
    scope: { kind: 'stream', id: 'st-zncon' },
    unit: '%',
    value: 0.06,
    spec: { hi: 0.1 },
    higherIsBetter: false,
    decimals: 2,
    latency: 'rush',
    provenance: 'Deck slide 8 smelter contract limits',
  },
  {
    id: 'rd-spec-tails-pb',
    tag: 'SA05',
    label: 'Pb in tailings',
    kind: 'quality',
    scope: { kind: 'stream', id: 'st-tails' },
    unit: '%',
    spec: { hi: 0.3 },
    higherIsBetter: false,
    decimals: 1,
    latency: 'shift',
    provenance: 'Module doc §8.4',
  },
  {
    id: 'rd-spec-tails-zn',
    tag: 'SA05',
    label: 'Zn in tailings',
    kind: 'quality',
    scope: { kind: 'stream', id: 'st-tails' },
    unit: '%',
    spec: { hi: 0.92 },
    higherIsBetter: false,
    decimals: 2,
    latency: 'shift',
    provenance:
      'Derived from Zn recovery target at PFS head grade — V8 sheet says 0.5%; [syn] target retied (seed.P5 #295)',
  },
  {
    id: 'rd-spec-tails-ag',
    tag: 'SA05',
    label: 'Ag in tailings',
    kind: 'quality',
    scope: { kind: 'stream', id: 'st-tails' },
    unit: 'oz/t',
    spec: { hi: 0.1 },
    higherIsBetter: false,
    decimals: 3,
    latency: 'shift',
    provenance:
      '[syn] Ag tails ceiling — design-basis loss at PFS head grade is 0.081 oz/t (seed.P2 #291)',
  },
  {
    id: 'rd-spec-pb-sb',
    tag: 'SA03',
    label: 'Sb (penalty element)',
    kind: 'quality',
    scope: { kind: 'stream', id: 'st-pbcon' },
    unit: '%',
    value: 0.72,
    spec: { hi: 1.5 },
    higherIsBetter: false,
    decimals: 2,
    latency: 'rush',
    provenance:
      '[syn] month-six assay inside contract — typical-trigger band 0.5–1.0% Pb concentrate (Paul V8 Penalty Element tab)',
  },
  {
    id: 'rd-spec-pb-bi',
    tag: 'SA03',
    label: 'Bi (penalty element)',
    kind: 'quality',
    scope: { kind: 'stream', id: 'st-pbcon' },
    unit: '%',
    value: 0.06,
    spec: { hi: 0.15 },
    higherIsBetter: false,
    decimals: 2,
    latency: 'rush',
    provenance:
      '[syn] month-six assay inside contract — typical-trigger band 0.05–0.1% Pb concentrate (Paul V8 Penalty Element tab)',
  },
  {
    id: 'rd-spec-pb-hg',
    tag: 'SA03',
    label: 'Hg (penalty element)',
    kind: 'quality',
    scope: { kind: 'stream', id: 'st-pbcon' },
    unit: 'ppm',
    value: 4,
    spec: { hi: 15 },
    higherIsBetter: false,
    decimals: 0,
    latency: 'rush',
    provenance:
      '[syn] illustrative smelter schedule — see decisions.md § Bound by Miles, Money is seeded',
  },
  {
    id: 'rd-spec-zn-hg',
    tag: 'SA04',
    label: 'Hg (penalty element)',
    kind: 'quality',
    scope: { kind: 'stream', id: 'st-zncon' },
    unit: 'ppm',
    value: 7,
    spec: { hi: 15 },
    higherIsBetter: false,
    decimals: 0,
    latency: 'rush',
    provenance:
      '[syn] month-six assay inside contract — typical-trigger band 5–10 ppm Zn concentrate (Paul V8 Penalty Element tab)',
  },
  {
    id: 'rd-spec-zn-f',
    tag: 'SA04',
    label: 'F (penalty element)',
    kind: 'quality',
    scope: { kind: 'stream', id: 'st-zncon' },
    unit: 'ppm',
    value: 245,
    spec: { hi: 400 },
    higherIsBetter: false,
    decimals: 0,
    latency: 'rush',
    provenance:
      '[syn] month-six assay inside contract — typical-trigger band 200–300 ppm Zn concentrate (Paul V8 Penalty Element tab)',
  },
  {
    id: 'rd-spec-zn-cl',
    tag: 'SA04',
    label: 'Cl (penalty element)',
    kind: 'quality',
    scope: { kind: 'stream', id: 'st-zncon' },
    unit: '%',
    value: 0.04,
    spec: { hi: 0.08 },
    higherIsBetter: false,
    decimals: 2,
    latency: 'rush',
    provenance:
      '[syn] month-six assay inside contract — typical-trigger band 0.03–0.05% Zn concentrate (Paul V8 Penalty Element tab)',
  },
  {
    id: 'rd-spec-zn-cd',
    tag: 'SA04',
    label: 'Cd (penalty element)',
    kind: 'quality',
    scope: { kind: 'stream', id: 'st-zncon' },
    unit: '%',
    value: 0.22,
    spec: { hi: 0.5 },
    higherIsBetter: false,
    decimals: 2,
    latency: 'rush',
    provenance:
      '[syn] month-six assay inside contract — typical-trigger band 0.2–0.3% Zn concentrate (Paul V8 Penalty Element tab)',
  },
  {
    id: 'rd-spec-zn-sb',
    tag: 'SA04',
    label: 'Sb (penalty element)',
    kind: 'quality',
    scope: { kind: 'stream', id: 'st-zncon' },
    unit: '%',
    value: 0.03,
    spec: { hi: 0.08 },
    higherIsBetter: false,
    decimals: 2,
    latency: 'rush',
    provenance:
      '[syn] month-six assay inside contract — typical-trigger band 0.02–0.05% Zn concentrate (Paul V8 Penalty Element tab)',
  },
  {
    id: 'rd-jt023-kwpt',
    tag: 'JT-023',
    label: 'Mill specific energy',
    kind: 'ratio',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: 'kW/tph',
    value: 12.8,
    design: 12.0,
    band: { lo: 10, hi: 14 },
    higherIsBetter: false,
    decimals: 1,
    latency: 'online',
    provenance: 'Deck slide 12 · hardness walkthrough',
  },
  {
    id: 'rd-cyc-of-p80-daily',
    tag: 'SA01-6W',
    label: 'Cyclone O/F P80 (six-week trend)',
    kind: 'size',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: 'µm',
    value: 81,
    design: 74,
    band: { lo: 70, hi: 85 },
    higherIsBetter: false,
    decimals: 1,
    latency: 'composite',
    provenance: '[syn] six-week cyclone O/F P80 wear drift — overlaps rs-sa01-p80 last 15 d',
  },
  {
    id: 'rd-cyc-pump-vsd',
    tag: 'P-101',
    label: 'Cyclone feed pump VSD',
    kind: 'ratio',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: '%',
    value: 71,
    design: 65,
    band: { lo: 55, hi: 75 },
    higherIsBetter: false,
    decimals: 0,
    latency: 'online',
    provenance: '[syn] cyclone feed pump VSD at constant discharge pressure — six-week wear drift series',
  },
  {
    id: 'rd-unit-cr01-rate',
    tag: 'CR01',
    label: 'Crusher throughput',
    kind: 'rate',
    scope: { kind: 'subprocess', id: 'sp-crush' },
    unit: 't/h',
    value: 680,
    latency: 'online',
    provenance: 'Deck slide 12',
  },
  {
    id: 'rd-unit-cr01-avail',
    tag: 'CR01',
    label: 'Crusher availability',
    kind: 'ratio',
    scope: { kind: 'subprocess', id: 'sp-crush' },
    unit: '% avail',
    value: 100,
    latency: 'online',
    provenance: 'Deck slide 12',
  },
  {
    id: 'rd-unit-bm01-feed',
    tag: 'WIT-021',
    label: 'Ball mill feed',
    kind: 'rate',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: 'stph',
    value: PLANT_ORE_READING.value! / 24,
    design: PLANT_ORE_READING.design! / 24,
    decimals: 1,
    band: MILL_FEED_BAND,
    higherIsBetter: true,
    latency: 'shift',
    provenance: '[derived] D14 fresh mill feed = rd-kpi-ore-td / 24; design from the same reading; normal band from Module 1 §8.2',
  },
  {
    id: 'rd-unit-cy01-sol',
    tag: 'DT-203',
    label: 'Cyclone feed solids',
    kind: 'density',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: '% sol.',
    value: 61,
    latency: 'online',
    provenance: 'Deck slide 12',
  },
  {
    id: 'rd-unit-cy01-p',
    tag: 'PT-173',
    label: 'Cyclone pressure',
    kind: 'pressure',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: 'kPa',
    value: 84,
    band: { lo: 82, hi: 92 },
    latency: 'online',
    provenance: 'Deck slide 12',
  },
  {
    id: 'rd-unit-rg01-rate',
    tag: 'RG01',
    label: 'Regrind feed',
    kind: 'rate',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: 't/h',
    value: 18,
    latency: 'online',
    provenance: 'Deck slide 12',
  },
  {
    id: 'rd-unit-rg01-p80',
    tag: 'DT-605',
    label: 'Regrind P80',
    kind: 'size',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: 'µm',
    value: 30,
    band: { lo: 27, hi: 33 },
    latency: 'shift',
    provenance: 'DCS FD §8.5 process-flow concept (~30 µm) — deck design says 38 µm, band 35-42',
  },
  {
    id: 'rd-unit-pb01-ph',
    // The tag map is the authority for an instrument tag (#735): `pb-rougher-ph`
    // (`bunker-hill-spc.ts` TAG_MAPPING) names this instrument AT-128 — the Lead
    // Rougher Feed pH transmitter, DCS functional description Appendix E,
    // PID-32N-400. AT-185 was the deck's own number for the same instrument; the
    // reading's tag was the last place it survived (P17 comp adjudication 13
    // deferred it; discrepancy row 341).
    tag: 'AT-128',
    label: 'Pb float pH',
    kind: 'ph',
    scope: { kind: 'subprocess', id: 'sp-pb' },
    unit: 'pH',
    value: 8.4,
    band: { lo: 8.2, hi: 8.6 },
    decimals: 1,
    latency: 'online',
    provenance: 'Deck slide 12',
  },
  {
    id: 'rd-unit-pb01-rec',
    tag: 'kpi-pb-rec',
    label: 'Pb recovery',
    kind: 'ratio',
    scope: { kind: 'subprocess', id: 'sp-pb' },
    unit: '%',
    value: 85.1,
    design: 88.2,
    higherIsBetter: true,
    decimals: 1,
    latency: 'shift',
    provenance: 'PFS §7.1 design (deck design says 87.0%)',
  },
  {
    id: 'rd-unit-zn01-ph',
    tag: 'AT-150',
    label: 'Zn float pH',
    kind: 'ph',
    scope: { kind: 'subprocess', id: 'sp-zn' },
    unit: 'pH',
    value: 10.2,
    band: { lo: 10.8, hi: 11.2 },
    decimals: 1,
    latency: 'online',
    provenance: 'Deck slide 12',
  },
  {
    id: 'rd-unit-zn01-rec',
    tag: 'kpi-zn-rec',
    label: 'Zn recovery',
    kind: 'ratio',
    scope: { kind: 'subprocess', id: 'sp-zn' },
    unit: '%',
    value: 82.3,
    higherIsBetter: true,
    decimals: 1,
    latency: 'shift',
    provenance: 'Deck slide 12',
  },
  {
    id: 'rd-unit-tkzn-uf',
    tag: 'TK-ZN',
    label: 'Zn thickener underflow',
    kind: 'density',
    scope: { kind: 'subprocess', id: 'sp-filter' },
    unit: '% UF',
    value: 58,
    latency: 'shift',
    provenance: 'Deck slide 12',
  },
  {
    id: 'rd-unit-flzn-moist',
    tag: 'FL-ZN',
    label: 'Zn filter moisture',
    kind: 'quality',
    scope: { kind: 'subprocess', id: 'sp-filter' },
    unit: '% moisture',
    value: 11.2,
    design: 10.0,
    target: 10.0,
    targetBasis: 'budget',
    higherIsBetter: false,
    decimals: 1,
    latency: 'shift',
    provenance: 'Deck slide 12 · flow strip Moist 11.2 %',
  },
  {
    id: 'rd-unit-ps01-paste',
    tag: 'PS01',
    label: 'Paste backfill',
    kind: 'rate',
    scope: { kind: 'subprocess', id: 'sp-paste' },
    unit: 't/d backfill',
    value: 318,
    target: 350,
    targetBasis: 'budget',
    higherIsBetter: true,
    latency: 'shift',
    provenance:
      'Deck slide 12 [Budget track: Month-4 ramp target adopted under pl.D1 #142, 2026-09-09]',
  },
  {
    id: 'rd-flow-cyc-uf',
    tag: 'FIT-203',
    label: 'Cyclone underflow',
    kind: 'rate',
    scope: { kind: 'stream', id: 'st-cyc-uf' },
    unit: 'stph',
    value: 186,
    latency: 'online',
    provenance: 'Module 1 §8.1 mass balance',
  },
  {
    id: 'rd-flow-rom',
    tag: 'ROM',
    label: 'ROM ore',
    kind: 'rate',
    scope: { kind: 'stream', id: 'st-rom' },
    unit: 'stpd',
    value: 1800,
    design: 1800,
    latency: 'shift',
    provenance: 'Module 1 §8.1',
  },
  {
    id: 'rd-flow-crushed',
    tag: 'FOB',
    label: 'Crushed ore to FOB',
    kind: 'rate',
    scope: { kind: 'stream', id: 'st-crushed' },
    unit: 'stph',
    value: 75,
    design: 75,
    latency: 'online',
    provenance: 'Module 1 §8.1',
  },
  PLANT_ORE_READING,
  {
    id: 'rd-kpi-zn-rec',
    tag: 'kpi-zn-rec',
    label: 'Zn recovery',
    kind: 'ratio',
    scope: { kind: 'plant' },
    unit: '%',
    value: 82.3,
    design: 85.1,
    target: 69.8,
    targetBasis: 'budget',
    decimals: 1,
    latency: 'shift',
    provenance:
      'PFS §7.1 design 85.1% at 58.0% Zn; ramp = 82% of design (FD §8.1 Zn circuit) = 69.8% (deck design says 90.0%) [Budget track: Month-4 ramp target adopted under pl.D1 #142, 2026-09-09]',
  },
  {
    id: 'rd-kpi-pb-rec',
    tag: 'kpi-pb-rec',
    label: 'Pb recovery',
    kind: 'ratio',
    scope: { kind: 'plant' },
    unit: '%',
    value: 85.1,
    design: 88.2,
    target: 75.0,
    targetBasis: 'budget',
    decimals: 1,
    latency: 'shift',
    provenance:
      'PFS §7.1 design 88.2% at 67.0% Pb; ramp = 85% of design = 75.0% (deck design says 87.0%) [Budget track: Month-4 ramp target adopted under pl.D1 #142, 2026-09-09]',
  },
  {
    id: 'rd-kpi-ag-rec',
    tag: 'kpi-ag-rec',
    label: 'Ag recovery',
    kind: 'ratio',
    scope: { kind: 'plant' },
    unit: '%',
    value: 83.6,
    design: 84.2,
    target: 71.6,
    targetBasis: 'budget',
    decimals: 1,
    latency: 'shift',
    provenance:
      'PFS §7.1 design 84.2% at 12.13 oz/t; ramp = 85% of design = 71.6% (deck design says 94.0%) [Budget track: Month-4 ramp target adopted under pl.D1 #142, 2026-09-09]',
  },
  {
    id: 'rd-kpi-zn-grade',
    tag: 'kpi-zn-grade',
    label: 'Zn conc grade',
    kind: 'quality',
    scope: { kind: 'plant' },
    unit: '% Zn',
    value: 58.4,
    design: 58.0,
    // No target / targetBasis: the FD sets no budget comparator for Zn
    // concentrate (Paul states none) — asserting one under either basis
    // would invent a level the corpus does not contain. Resolves to
    // 'design', the one basis this figure actually is (pl.D1 round 3, #142,
    // 2026-09-09; see docs/plans/route-reads/reviews/planning-flip.md
    // question 3).
    decimals: 1,
    latency: 'rush',
    provenance:
      'PFS §7.1 design 58.0% Zn (deck design says 52.0%); no budget comparator is set for concentrate grade',
  },
  {
    id: 'rd-kpi-pb-grade',
    tag: 'kpi-pb-grade',
    label: 'Pb conc grade',
    kind: 'quality',
    scope: { kind: 'plant' },
    unit: '% Pb',
    value: 67.1,
    design: 67.0,
    // No target / targetBasis — see rd-kpi-zn-grade above: the FD gives no
    // ramp or budget grade for Pb concentrate either (pl.D1 round 3, #142).
    decimals: 1,
    latency: 'rush',
    provenance:
      'PFS §7.1 design 67.0% Pb (deck design says 65.0%); no budget comparator is set for concentrate grade',
  },
  {
    id: 'rd-kpi-ag-grade',
    tag: 'kpi-ag-grade',
    label: 'Ag in Pb/Ag conc',
    kind: 'quality',
    scope: { kind: 'plant' },
    unit: 'oz/t',
    // K20 (#717): the live Ag grade is the balance's own product grade at the
    // cursor — head 0.442 oz/st × recovery 83.6 % ÷ concentrate mass pull
    // (52.3536 ÷ 1,720) = 12.1398 oz/t → 12.14. The SA03 stream reading
    // `rd-spec-ag-grade` carries the same figure, so the Shift report and
    // Metal accounting's product grade render one number. The 11.3 it
    // replaced was the deck's live LIMS figure and did not close the day
    // balance (12.14 vs 11.3 = 6.92 % off).
    value: 12.14,
    design: 12.13,
    // No target / targetBasis — see rd-kpi-zn-grade above: the FD gives no
    // ramp or budget grade for Ag in Pb/Ag concentrate either (pl.D1 round
    // 3, #142).
    // decimals is 2 (K20, #717): the live value is now the balance's own
    // 2 dp figure, so 1 dp would truncate it. The earlier 1 was "sized to
    // the live LIMS value (11.3)" when that value was 1 dp, and bumping it
    // was reverted (pl.D1 #142 round 4) because the reading then carried a
    // 1 dp live value beside a 2 dp design (12.13) and the window mean
    // rendered "11.79"/"11.80"; the live value now has the design's own
    // precision, so both render 2 dp and the concern is gone.
    // `designMarkerDecimals` in RoleLandingPanels.transform.ts still takes
    // max(reading.decimals, precisionFromNumber(design)) for the marker.
    decimals: 2,
    latency: 'rush',
    provenance:
      'PFS §7.1 design 12.13 oz/t (deck design says 32.0); no budget comparator is set for concentrate grade',
  },
  {
    id: 'rd-kpi-mill-util',
    tag: 'kpi-mill-util',
    label: 'Mill utilisation',
    kind: 'ratio',
    scope: { kind: 'plant' },
    unit: '%',
    value: 92.1,
    design: 92.0,
    target: 92.0,
    targetBasis: 'budget',
    decimals: 1,
    latency: 'shift',
    provenance:
      'Deck slide 5 startup table [Budget track: Month-4 ramp target adopted under pl.D1 #142, 2026-09-09]',
  },
  {
    id: 'rd-kpi-milled-day',
    tag: 'kpi-milled-day',
    label: 'Milled tonnes day shift',
    kind: 'rate',
    scope: { kind: 'plant' },
    unit: 't',
    value: 870,
    // 900 t/shift = 1,800 t/d nameplate ÷ 24h × 12h shift — a design-basis
    // figure, not ramp/2 (720 t/shift). `targetBasis` has no 'design' value
    // (design is its own field by schema convention — comparison-basis.ts
    // resolves it as a candidate only via `design`, never via `target`), so
    // this moves from a mistagged `target` to the `design` field it
    // actually is (ruling #258 round 2, "the shift target is design").
    design: 900,
    latency: 'shift',
    provenance: 'Deck slide 3 plant-wide table',
  },
  {
    id: 'rd-kpi-milled-night',
    tag: 'kpi-milled-night',
    label: 'Milled tonnes night shift',
    kind: 'rate',
    scope: { kind: 'plant' },
    unit: 't',
    value: 850,
    // Same basis correction as rd-kpi-milled-day (ruling #258 round 2).
    design: 900,
    latency: 'shift',
    provenance: 'Deck slide 3 plant-wide table',
  },
  {
    id: 'rd-kpv-bm-feed',
    tag: 'WIT-021',
    label: 'Ball mill feed rate',
    kind: 'rate',
    scope: { kind: 'plant' },
    unit: 'stph',
    value: PLANT_ORE_READING.value! / 24,
    design: PLANT_ORE_READING.design! / 24,
    decimals: 1,
    band: MILL_FEED_BAND,
    latency: 'shift',
    provenance: '[derived] D14 fresh mill feed = rd-kpi-ore-td / 24; design from the same reading; normal band from Module 1 §8.2',
  },
  {
    id: 'rd-kpv-mill-dens',
    tag: 'DT-203',
    label: 'Mill discharge density',
    kind: 'density',
    scope: { kind: 'plant' },
    unit: '%',
    value: 72.1,
    design: 72,
    band: { lo: 70, hi: 74 },
    decimals: 1,
    latency: 'online',
    provenance: 'Deck slide 12 KPV table',
  },
  {
    id: 'rd-kpv-cyc-p',
    tag: 'PT-173',
    label: 'Cyclone pressure',
    kind: 'pressure',
    scope: { kind: 'plant' },
    unit: 'kPa',
    value: 84,
    band: { lo: 82, hi: 92 },
    latency: 'online',
    provenance: 'Deck slide 12 KPV table',
  },
  {
    id: 'rd-kpv-regrind-p80',
    tag: 'DT-605',
    label: 'Regrind P80',
    kind: 'size',
    scope: { kind: 'plant' },
    unit: 'µm',
    value: 30,
    band: { lo: 27, hi: 33 },
    latency: 'shift',
    provenance: 'DCS FD §8.5 process-flow concept (~30 µm) — deck design says 38 µm, band 35-42',
  },
  {
    id: 'rd-kpv-pb-ph',
    // See `rd-unit-pb01-ph` above: the tag map names this reading's instrument
    // AT-128 (#735, discrepancy row 341). The Process Readings card and the
    // Senior metallurgist landing's Pb/Ag row both print this tag, so they move
    // with it; the deck's AT-185 survives only where a surface documents that it
    // is naming its source's own dialect (the Plant model's C2 node display,
    // `node-display.ts` § tagIds, and the UI-concept insight tags).
    tag: 'AT-128',
    label: 'Pb float pH',
    kind: 'ph',
    scope: { kind: 'plant' },
    unit: 'pH',
    value: 8.4,
    band: { lo: 8.2, hi: 8.6 },
    latency: 'online',
    provenance: 'Deck slide 12 KPV table',
  },
  {
    id: 'rd-kpv-zn-ph',
    tag: 'AT-150',
    label: 'Zn float pH',
    kind: 'ph',
    scope: { kind: 'plant' },
    unit: 'pH',
    value: 10.2,
    band: { lo: 10.8, hi: 11.2 },
    decimals: 1,
    latency: 'online',
    provenance: 'Deck slide 12 KPV table',
  },
  {
    id: 'rd-kpv-ag-grade',
    tag: 'SA03',
    label: 'Ag grade Pb/Ag conc',
    kind: 'quality',
    scope: { kind: 'plant' },
    unit: 'oz/t',
    value: 12.14,
    // Smelter payability floor re-tied to the PFS basis: the deck's 25 oz/t
    // floor was authored against its own 32.0 oz/t design and sits above the
    // whole PFS-basis smelter band (`bunker-hill-grade-recovery-curves.ts`
    // `wc-ag.smelterGradeBand` 10.0-13.0), so it would read permanently
    // off-spec against a 12.13 oz/t design (ruling #258).
    spec: { lo: 10 },
    decimals: 2,
    latency: 'rush',
    provenance: 'Deck slide 12 KPV table — PFS-basis smelter floor 10 oz/t (deck design says 25 oz/t floor on a 32.0 oz/t design)',
  },
  {
    id: 'rd-kpv-zn-grade',
    tag: 'SA04',
    label: 'Zn conc grade',
    kind: 'quality',
    scope: { kind: 'plant' },
    unit: '% Zn',
    value: 58.4,
    // polish.P15 r1 (item 9): nothing reads this spec; the one Zn concentrate
    // floor is the ConcentrateSpec's `cs-zn` > 55 % Zn, so the seed holds that
    // one value rather than the deck row's untraced `>51`.
    spec: { lo: 55 },
    decimals: 1,
    latency: 'rush',
    provenance: 'Deck slide 12 KPV table',
  },
  {
    id: 'rd-reag-znso4',
    tag: 'ZnSO4',
    label: 'ZnSO₄',
    kind: 'rate',
    scope: { kind: 'plant' },
    unit: 'g/t',
    value: 310,
    design: 300,
    latency: 'shift',
    provenance: 'Deck slide 7 reagent table',
  },
  {
    id: 'rd-reag-smbs',
    tag: 'SMBS',
    label: 'SMBS',
    shortLabel: 'SMBS dose',
    kind: 'rate',
    scope: { kind: 'plant' },
    unit: 'g/t',
    value: 195,
    design: 200,
    latency: 'shift',
    provenance: 'Deck slide 7 reagent table',
  },
  {
    id: 'rd-reag-cuso4',
    tag: 'CuSO4',
    label: 'CuSO₄',
    kind: 'rate',
    scope: { kind: 'plant' },
    unit: 'g/t',
    value: 155,
    design: 150,
    latency: 'shift',
    provenance: 'Deck slide 7 reagent table',
  },
  {
    id: 'rd-cuso4-batch-strength',
    tag: 'CuSO4-BATCH',
    label: 'CuSO₄ batch strength',
    shortLabel: 'CuSO₄ batch',
    kind: 'quality',
    scope: { kind: 'plant' },
    unit: 'g/L',
    value: 162,
    design: 180,
    higherIsBetter: true,
    latency: 'shift',
    provenance: 'Issue spine iss-cuso4-batch — titration at 09:45',
  },
  {
    id: 'rd-reag-mibc',
    tag: 'MIBC',
    label: 'MIBC',
    kind: 'rate',
    scope: { kind: 'plant' },
    unit: 'g/t',
    value: 24,
    design: 25,
    latency: 'shift',
    provenance: 'Deck slide 7 reagent table',
  },
  {
    id: 'rd-reag-3418a',
    tag: '3418A',
    label: '3418A',
    kind: 'rate',
    scope: { kind: 'plant' },
    unit: 'g/t',
    value: 42,
    design: 45,
    latency: 'shift',
    provenance: 'Deck slide 7 reagent table',
  },
  {
    id: 'rd-reag-aero',
    tag: 'Aero5100',
    label: 'Aero 5100',
    kind: 'rate',
    scope: { kind: 'plant' },
    unit: 'g/t',
    value: 31,
    design: 35,
    latency: 'shift',
    provenance: 'Deck slide 7 reagent table',
  },
  {
    id: 'rd-reag-lime',
    tag: 'Lime',
    label: 'Lime',
    kind: 'rate',
    scope: { kind: 'plant' },
    unit: 'g/t',
    value: 2100,
    design: 2000,
    latency: 'shift',
    provenance: 'Deck slide 7 reagent table',
  },
  {
    id: 'rd-util-cyc',
    tag: 'CY-CLSTR',
    label: 'Cyclone cluster spare capacity',
    kind: 'ratio',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: '%',
    value: 80,
    latency: 'online',
    provenance: '[syn] — 4 of 5 in service at design; one spare only',
  },
  {
    id: 'rd-util-pb',
    tag: 'kpi-pb-through',
    label: 'Pb/Ag flotation',
    kind: 'rate',
    scope: { kind: 'subprocess', id: 'sp-pb' },
    unit: '%',
    value: 44,
    latency: 'shift',
    provenance: '[syn] — no documented cell-bank ceiling in the corpus; invented for ranking completeness',
  },
  {
    id: 'rd-util-zn',
    tag: 'kpi-zn-through',
    label: 'Zn flotation',
    kind: 'rate',
    scope: { kind: 'subprocess', id: 'sp-zn' },
    unit: '%',
    value: 48,
    latency: 'shift',
    provenance: '[syn] — no documented cell-bank ceiling in the corpus; invented for ranking completeness',
  },

  // --- paul-email.A3 #248 -- stage-variable seed (grinding output through Zn cleaner) ---
  // Grinding output (mill feed head grades + waste + flow/density gauge + pH).
  {
    id: 'rd-head-zn',
    tag: 'HD-ZN',
    label: 'Head Zn grade (mill feed)',
    kind: 'quality',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: '% Zn',
    value: 5.44,
    design: 5.44,
    higherIsBetter: true,
    decimals: 2,
    band: { lo: 4.9, hi: 6.0 },
    latency: 'shift',
    provenance:
      'A section M2.6.2 design feed-grade table (PFS 5.44% Zn) -- pinned to bunker-hill-accounting-seed RECONCILIATION_CHECK rcr-feed-grade; [syn] instrument tag, no head-assay tag documented',
  },
  {
    id: 'rd-head-pb',
    tag: 'HD-PB',
    label: 'Head Pb grade (mill feed)',
    kind: 'quality',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: '% Pb',
    value: 2.4,
    design: 2.4,
    higherIsBetter: true,
    decimals: 2,
    band: { lo: 2.1, hi: 2.7 },
    latency: 'shift',
    provenance:
      'A section M2.6.2 design feed-grade table (PFS 2.40% Pb) -- pinned to bunker-hill-accounting-seed RECONCILIATION_CHECK rcr-feed-grade; [syn] instrument tag, no head-assay tag documented',
  },
  {
    id: 'rd-head-ag',
    tag: 'HD-AG',
    label: 'Head Ag grade (mill feed)',
    kind: 'quality',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: 'oz/st',
    value: 0.442,
    design: 0.455,
    higherIsBetter: true,
    decimals: 3,
    band: { lo: 0.35, hi: 0.55 },
    latency: 'shift',
    provenance:
      'PFS 2022 feed-grade table says 1.00 oz/t — mass-balance-consistent figure from concentrate route at demo cursor (seed.P2 #291); [syn] instrument tag',
  },
  {
    id: 'rd-head-as',
    tag: 'HD-AS',
    label: 'Head As grade (mill feed)',
    kind: 'quality',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: '% As',
    value: 0.08,
    design: 0.08,
    higherIsBetter: false,
    decimals: 2,
    band: { lo: 0.05, hi: 0.12 },
    latency: 'shift',
    provenance:
      '[syn] plant-plausible arsenopyrite-association head grade for a Pb-Zn-Ag sulfide vein deposit -- well below the 0.5% smelter penalty on the Pb concentrate (rd-spec-pb-as) since flotation upgrades it several-fold; no head-assay figure documented',
  },
  {
    id: 'rd-head-sio2',
    tag: 'HD-SIO2',
    label: 'Head SiO2 grade (mill feed)',
    kind: 'quality',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: '% SiO2',
    value: 42.0,
    design: 42.0,
    higherIsBetter: false,
    decimals: 1,
    band: { lo: 38, hi: 46 },
    latency: 'shift',
    provenance:
      '[syn] plant-plausible siliceous gangue fraction for a quartzite-hosted Pb-Zn-Ag vein deposit -- the dominant waste mineral by mass; no head-assay figure documented',
  },
  {
    id: 'rd-feed-flow',
    tag: 'FIT-204',
    label: 'Cyclone overflow slurry flow',
    kind: 'rate',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: 'm3/h',
    value: 210,
    design: 210,
    decimals: 0,
    band: { lo: 180, hi: 240 },
    latency: 'online',
    provenance:
      "[syn] numbered after the cyclone-feed flow meter FIT-203 (bunker-hill-accounting-seed ms-fit203) -- the flow-meter half of Paul's flow/density pair for grinding-circuit output volume; no cyclone-overflow flow figure documented",
  },
  {
    id: 'rd-co-dens',
    tag: 'DT-204',
    label: 'Cyclone overflow density',
    kind: 'density',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: '% solids',
    value: 32,
    design: 32,
    decimals: 0,
    band: { lo: 28, hi: 36 },
    latency: 'online',
    provenance:
      "[syn] numbered after the cyclone-feed density gauge DT-203 (ms-dt203) -- the density-gauge half of Paul's flow/density pair; no cyclone-overflow density figure documented",
  },
  {
    id: 'rd-grind-ph',
    tag: 'AT-186',
    label: 'Grinding circuit output pH',
    kind: 'ph',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: 'pH',
    value: 7.8,
    design: 7.8,
    decimals: 1,
    band: { lo: 7.4, hi: 8.2 },
    latency: 'online',
    provenance:
      '[syn] numbered near AT-185 (Pb float pH) / AT-150 (Zn float pH) -- grinding-output pH ahead of Pb conditioning (paul-call.A9); no grinding-stage pH tag documented',
  },

  // Pb conditioner and rougher.
  {
    id: 'rd-pb-rgh-dens',
    tag: 'DT-402',
    label: 'Pb rougher pulp density',
    kind: 'density',
    scope: { kind: 'subprocess', id: 'sp-pb' },
    unit: '% solids',
    value: 33,
    design: 33,
    decimals: 0,
    band: { lo: 30, hi: 36 },
    latency: 'online',
    provenance:
      '[syn] plant-plausible rougher-feed pulp density (SA02 sample-point circuit) -- no Appendix B density tag documented for the Pb rougher',
  },
  {
    id: 'rd-pb-rgh-air',
    tag: 'FI-403',
    label: 'Pb rougher air flow',
    kind: 'rate',
    scope: { kind: 'subprocess', id: 'sp-pb' },
    unit: 'm3/min',
    value: 12,
    design: 12,
    decimals: 0,
    band: { lo: 9, hi: 15 },
    latency: 'online',
    provenance:
      '[syn] plant-plausible rougher cell-bank air flow -- no Appendix B air-flow tag documented for the Pb rougher',
  },
  {
    id: 'rd-pb-rgh-level',
    tag: 'LT-401',
    label: 'Pb rougher cell level',
    kind: 'level',
    scope: { kind: 'subprocess', id: 'sp-pb' },
    unit: '%',
    value: 60,
    design: 60,
    decimals: 0,
    band: { lo: 50, hi: 70 },
    latency: 'online',
    provenance:
      '[syn] representative rougher cell-bank level, distinct from the per-cell loop tags LIC4010 / LIC4013 already in CONTROL_LOOPS -- no bank-level tag documented',
  },
  {
    id: 'rd-pb-rgh-froth',
    tag: 'FR-PB01',
    label: 'Pb rougher froth depth',
    kind: 'level',
    scope: { kind: 'subprocess', id: 'sp-pb' },
    unit: 'mm',
    value: 200,
    design: 200,
    decimals: 0,
    band: { lo: 150, hi: 250 },
    latency: 'shift',
    provenance:
      '[syn] plant-plausible operator-round froth depth -- froth depth is normally rodded, not continuously instrumented; no froth-depth tag documented',
  },
  {
    id: 'rd-pb-rgh-con-pb',
    tag: 'SA06',
    label: 'Pb rougher concentrate grade',
    kind: 'quality',
    scope: { kind: 'subprocess', id: 'sp-pb' },
    unit: '% Pb',
    value: 38.0,
    design: 38.0,
    higherIsBetter: true,
    decimals: 1,
    band: { lo: 33, hi: 43 },
    latency: 'shift',
    provenance:
      'Module 1 section 8.2 sample point SA06 (Pb rougher concentrate, every 4 h) -- [syn] value, no documented rougher-concentrate assay figure',
  },
  {
    id: 'rd-pb-rgh-con-zn',
    tag: 'SA06',
    label: 'Zn contamination of Pb rougher concentrate',
    kind: 'quality',
    scope: { kind: 'subprocess', id: 'sp-pb' },
    unit: '% Zn',
    value: 4.5,
    design: 4.5,
    higherIsBetter: false,
    decimals: 1,
    band: { lo: 2.0, hi: 7.0 },
    latency: 'shift',
    provenance:
      'Module 1 section 8.2 sample point SA06 (same sample as rd-pb-rgh-con-pb) -- [syn] value, no documented figure',
  },

  // Pb regrind and cleaner.
  {
    id: 'rd-pb-con-zn',
    tag: 'SA03',
    label: 'Zn contamination of final Pb concentrate',
    kind: 'quality',
    scope: { kind: 'subprocess', id: 'sp-pb' },
    unit: '% Zn',
    value: 1.2,
    design: 1.2,
    higherIsBetter: false,
    decimals: 1,
    band: { lo: 0.5, hi: 2.0 },
    latency: 'rush',
    provenance:
      'Module 1 section 8.4 sample point SA03 -- same sample and latency as rd-kpi-pb-grade / rd-spec-pb-grade; [syn] value, no documented Zn-in-Pb-concentrate figure',
  },

  // Zn conditioner and rougher.
  {
    id: 'rd-zn-rgh-dens',
    tag: 'DT-502',
    label: 'Zn rougher pulp density',
    kind: 'density',
    scope: { kind: 'subprocess', id: 'sp-zn' },
    unit: '% solids',
    value: 30,
    design: 30,
    decimals: 0,
    band: { lo: 27, hi: 33 },
    latency: 'online',
    provenance:
      '[syn] plant-plausible rougher-feed pulp density -- no Appendix B density tag documented for the Zn rougher',
  },
  {
    id: 'rd-zn-rgh-air',
    tag: 'FI-503',
    label: 'Zn rougher air flow',
    kind: 'rate',
    scope: { kind: 'subprocess', id: 'sp-zn' },
    unit: 'm3/min',
    value: 14,
    design: 14,
    decimals: 0,
    band: { lo: 10, hi: 18 },
    latency: 'online',
    provenance:
      '[syn] plant-plausible rougher cell-bank air flow -- no Appendix B air-flow tag documented for the Zn rougher',
  },
  {
    id: 'rd-zn-rgh-level',
    tag: 'LT-501',
    label: 'Zn rougher cell level',
    kind: 'level',
    scope: { kind: 'subprocess', id: 'sp-zn' },
    unit: '%',
    value: 62,
    design: 62,
    decimals: 0,
    band: { lo: 52, hi: 72 },
    latency: 'online',
    provenance:
      '[syn] representative rougher cell-bank level -- no bank-level tag documented',
  },
  {
    id: 'rd-zn-rgh-froth',
    tag: 'FR-ZN01',
    label: 'Zn rougher froth depth',
    kind: 'level',
    scope: { kind: 'subprocess', id: 'sp-zn' },
    unit: 'mm',
    value: 180,
    design: 180,
    decimals: 0,
    band: { lo: 130, hi: 230 },
    latency: 'shift',
    provenance:
      '[syn] plant-plausible operator-round froth depth -- froth depth is normally rodded, not continuously instrumented; no froth-depth tag documented',
  },
  {
    id: 'rd-zn-rgh-con-zn',
    tag: 'ZNRC-1',
    label: 'Zn rougher concentrate grade',
    kind: 'quality',
    scope: { kind: 'subprocess', id: 'sp-zn' },
    unit: '% Zn',
    value: 40.4,
    design: 42.0,
    higherIsBetter: true,
    decimals: 1,
    band: { lo: 37, hi: 47 },
    latency: 'shift',
    provenance:
      '[syn] plant-plausible Zn rougher-concentrate grade ahead of Zn cleaning -- covered by SA08 (Zn rougher concentrate, added `paul-call.A4` #253); latest value edited 42.0 -> 40.4 to seed the in-band divergence walk (deviation, see samples-build-report.md fix round 1)',
  },
  {
    id: 'rd-env-tsf-freeboard',
    tag: 'LT-TSF',
    label: 'TSF freeboard',
    kind: 'level',
    scope: { kind: 'plant' },
    unit: 'm',
    value: 2.4,
    spec: { lo: 2.0 },
    higherIsBetter: true,
    decimals: 1,
    latency: 'shift',
    provenance: '[syn] Illustrative permit TSF-01 freeboard minimum',
  },
  {
    id: 'rd-env-water-balance',
    tag: 'FIT-WBAL',
    label: 'Process water balance net',
    kind: 'rate',
    scope: { kind: 'subprocess', id: 'sp-water' },
    unit: 'm³/d',
    value: 120,
    band: { lo: -200, hi: 200 },
    latency: 'shift',
    provenance: '[syn] Site water balance — illustrative permit ENV-08',
  },
  {
    id: 'rd-env-discharge-ph',
    tag: 'ENV-PH',
    label: 'Discharge pH compliance samples',
    kind: 'count',
    scope: { kind: 'plant' },
    unit: 'samples',
    value: 28,
    spec: { lo: 28, hi: 28 },
    latency: 'shift',
    provenance: '[syn] Permit ENV-12 discharge condition count',
  },
  {
    id: 'rd-bm-brg-temp',
    tag: 'TE-622-625',
    label: 'Ball mill trunnion bearing temp',
    kind: 'level',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: '°C',
    value: 81.0,
    spec: { hi: 85 },
    band: { lo: 0, hi: 80 },
    higherIsBetter: false,
    decimals: 1,
    latency: 'online',
    provenance: '[syn] TE-622-625 · limit 85 °C · early warning > 80 °C (fm-trunnion)',
  },
  {
    id: 'rd-cm-oil-fe',
    tag: 'CM-OIL-FE',
    label: 'BM01 trunnion oil Fe',
    kind: 'quality',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: 'ppm Fe',
    value: 18.2,
    decimals: 1,
    latency: 'composite',
    provenance: '[syn] cm-1 monthly oil analysis',
  },
  {
    id: 'rd-cm-liner-vib',
    tag: 'CM-VIB-LIN',
    label: 'BM01 liner bolt vibration',
    kind: 'level',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: 'mm/s',
    value: 2.1,
    decimals: 1,
    latency: 'online',
    provenance: '[syn] cm-3 vibration baseline at liner change',
  },
  {
    id: 'rd-cm-stator-therm',
    tag: 'CM-THERM-ST',
    label: 'BM01 motor stator winding temp',
    kind: 'level',
    scope: { kind: 'subprocess', id: 'sp-grind' },
    unit: '°C',
    value: 62,
    decimals: 0,
    latency: 'online',
    provenance: '[syn] cm-4 annual thermography',
  },
  {
    id: 'rd-cm-zn-gearbox-oil',
    tag: 'CM-OIL-ZN',
    label: 'Zn rougher gearbox oil Fe',
    kind: 'quality',
    scope: { kind: 'subprocess', id: 'sp-zn' },
    unit: 'ppm Fe',
    value: 14.1,
    decimals: 1,
    latency: 'composite',
    provenance: '[syn] cm-6 quarterly oil analysis',
  },
  {
    id: 'rd-cm-wit-cal',
    tag: 'CM-WIT-CAL',
    label: 'WIT-021 calibration drift',
    kind: 'quality',
    scope: { kind: 'subprocess', id: 'sp-crush' },
    unit: '%',
    value: 1.2,
    decimals: 1,
    latency: 'shift',
    provenance: '[syn] cm-7 belt scale vs shift tonnage',
  },
];

export const UNIT_KEY_READING_IDS: Record<string, readonly string[]> = {
  CR01: ['rd-unit-cr01-rate', 'rd-unit-cr01-avail'],
  BM01: [
    'rd-unit-bm01-feed',
    'rd-spec-p80',
    'rd-head-zn',
    'rd-head-pb',
    'rd-head-ag',
    'rd-head-as',
    'rd-head-sio2',
  ],
  CY01: ['rd-unit-cy01-sol', 'rd-unit-cy01-p', 'rd-feed-flow', 'rd-co-dens', 'rd-grind-ph'],
  RG01: ['rd-unit-rg01-rate', 'rd-unit-rg01-p80'],
  PB01: [
    'rd-unit-pb01-ph',
    'rd-unit-pb01-rec',
    'rd-pb-rgh-dens',
    'rd-pb-rgh-air',
    'rd-pb-rgh-level',
    'rd-pb-rgh-froth',
    'rd-pb-rgh-con-pb',
    'rd-pb-rgh-con-zn',
    'rd-pb-con-zn',
  ],
  ZN01: [
    'rd-unit-zn01-ph',
    'rd-unit-zn01-rec',
    'rd-zn-rgh-dens',
    'rd-zn-rgh-air',
    'rd-zn-rgh-level',
    'rd-zn-rgh-froth',
    'rd-zn-rgh-con-zn',
  ],
  'TK-ZN': ['rd-unit-tkzn-uf'],
  'FL-ZN': ['rd-unit-flzn-moist'],
  PS01: ['rd-unit-ps01-paste'],
};

export const STREAM_FLOW_READING_IDS: Record<string, string> = {
  'st-rom': 'rd-flow-rom',
  'st-crushed': 'rd-flow-crushed',
  'st-cyc-uf': 'rd-flow-cyc-uf',
};
